import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';

const BUSINESS_TYPES = [
  'Restaurant / Takeaway',
  'Salon / Beauty',
  'Trades / Builder',
  'Clinic / Health',
  'Retail Shop',
  'Gym / Fitness',
  'Estate Agent',
  'Taxi / Delivery',
  'Consultant / Professional',
  'Event / Community',
  'Other Local Business'
];

const GOALS = [
  'Get more enquiries',
  'Send customers to menu',
  'Promote a special offer',
  'Collect leads',
  'Book appointments',
  'Send to WhatsApp',
  'Send to Smart Quote',
  'Drive TikTok / social traffic',
  'Launch a local campaign'
];

const DESTINATIONS = [
  'Smart Quote',
  'Website',
  'Menu',
  'Booking page',
  'WhatsApp',
  'Google review page',
  'Social media page',
  'Payment link',
  'Custom link'
];

type QRCampaignForm = {
  businessName: string;
  businessType: string;
  town: string;
  goal: string;
  offerTitle: string;
  offerDetails: string;
  destinationType: string;
  destinationUrl: string;
  contactName: string;
  email: string;
  phone: string;
};

const initialForm: QRCampaignForm = {
  businessName: '',
  businessType: 'Restaurant / Takeaway',
  town: '',
  goal: 'Promote a special offer',
  offerTitle: 'Launch Offer',
  offerDetails: 'Scan to unlock this offer and request details.',
  destinationType: 'Smart Quote',
  destinationUrl: 'https://nexoraxdigital.co.uk/smart-quote',
  contactName: '',
  email: '',
  phone: ''
};

const buildDestinationUrl = (form: QRCampaignForm) => {
  if (form.destinationType === 'Smart Quote') {
    const params = new URLSearchParams({
      source: 'qr-campaign',
      business: form.businessName,
      service: 'QR Campaign System'
    });

    return `https://nexoraxdigital.co.uk/smart-quote?${params.toString()}`;
  }

  if (form.destinationType === 'WhatsApp' && form.phone.trim()) {
    const phone = form.phone.replace(/[^0-9]/g, '');
    return `https://wa.me/${phone}`;
  }

  return form.destinationUrl || 'https://nexoraxdigital.co.uk/smart-quote';
};

export default function NexoraXQRCampaignGenerator() {
  const [form, setForm] = useState<QRCampaignForm>(initialForm);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedCampaign, setSavedCampaign] = useState<any>(null);

  const destination = useMemo(() => buildDestinationUrl(form), [form]);

  const campaign = useMemo(() => {
    const business = form.businessName || 'Your Business';
    const town = form.town || 'your local area';
    const type = form.businessType;
    const offer = form.offerTitle || 'Launch Offer';

    const headline = `Scan for ${business}'s ${offer}`;
    const subheadline = `${type} campaign for ${town}.`;
    const posterCopy = `Scan the QR code to view the offer, request details, book, message or start your NexoraX-powered digital journey.`;
    const flyerCopy = `${business} is launching a smarter way for customers to connect. Scan the QR code to access the offer, get more information, or request the next step instantly.`;
    const tikTokCaption = `Local business upgrade for ${business}. Scan the QR, unlock the offer, and see how digital campaigns can bring more enquiries. #LocalBusiness #QRCode #NexoraX #DigitalMarketing #${town.replace(/\s+/g, '')}`;
    const cta = form.destinationType === 'Smart Quote'
      ? 'Scan to request your digital preview'
      : form.goal === 'Send to WhatsApp'
        ? 'Scan to message us'
        : form.goal === 'Book appointments'
          ? 'Scan to book now'
          : 'Scan to unlock the offer';

    const bestUse = form.businessType.includes('Restaurant')
      ? 'Use on menus, counter cards, delivery bags, TikTok videos and window posters.'
      : form.businessType.includes('Trades')
        ? 'Use on vans, flyers, invoices, Facebook posts and local lead campaigns.'
        : form.businessType.includes('Salon')
          ? 'Use on salon mirrors, appointment cards, Instagram posts and reception displays.'
          : 'Use on posters, flyers, social posts, receipts, business cards and local campaign material.';

    return {
      headline,
      subheadline,
      posterCopy,
      flyerCopy,
      tikTokCaption,
      cta,
      bestUse,
      destination
    };
  }, [form, destination]);

  useEffect(() => {
    let cancelled = false;

    QRCode.toDataURL(destination, {
      width: 520,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: {
        dark: '#00f0ff',
        light: '#02030a'
      }
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl('');
      });

    return () => {
      cancelled = true;
    };
  }, [destination]);

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${label} copied.`);
      setTimeout(() => setNotice(''), 2500);
    } catch {
      setNotice('Clipboard blocked. Select and copy manually.');
    }
  };

  const saveCampaign = async () => {
    if (!form.businessName.trim()) {
      setNotice('Please add a business name before saving the QR campaign.');
      return;
    }

    setSaving(true);
    setNotice('');

    try {
      const response = await fetch('/api/qr-campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, campaign, destination })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Save failed.');
      }

      setSavedCampaign(data.campaign);
      setNotice(`QR campaign saved. Reference: ${data.campaign.reference}`);
    } catch {
      setNotice('QR campaign could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen overflow-hidden bg-[#02030a] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_12%_10%,rgba(0,240,255,0.18),transparent_35%),radial-gradient(circle_at_88%_18%,rgba(168,85,247,0.22),transparent_35%),linear-gradient(180deg,#02030a,#050014_55%,#02030a)]" />
      <div className="pointer-events-none fixed inset-0 opacity-30 [background-image:linear-gradient(rgba(0,240,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.05)_1px,transparent_1px)] [background-size:42px_42px]" />

      <section className="relative mx-auto max-w-7xl px-5 py-10 md:py-16">
        <div className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_60px_rgba(0,240,255,0.12)] backdrop-blur-xl md:p-10">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.45em] text-cyan-300">
                NexoraX QR Campaign Generator
              </p>
              <h1 className="mt-4 max-w-4xl text-4xl font-black leading-tight md:text-6xl">
                Turn a simple QR code into a full campaign.
              </h1>
              <p className="mt-5 max-w-3xl text-slate-300">
                Build QR posters, flyers, TikTok captions, offer wording and a scan-to-action campaign for local businesses.
              </p>
            </div>

            <div className="rounded-3xl border border-purple-500/30 bg-purple-950/20 p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">Campaign Flow</p>
              <p className="mt-2 text-2xl font-black text-cyan-300">Scan → Action → Lead</p>
              <a
                href="/smart-quote"
                className="mt-4 inline-flex rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50"
              >
                Start Smart Quote
              </a>
            </div>
          </div>
        </div>

        {notice && (
          <div className="mt-6 rounded-2xl border border-purple-500/30 bg-purple-950/20 p-4 text-sm text-purple-100">
            {notice}
          </div>
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(0,240,255,0.10)] backdrop-blur-xl">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
              Campaign setup
            </p>
            <h2 className="mt-2 text-2xl font-black">Build your QR campaign</h2>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <input
                value={form.businessName}
                onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                placeholder="Business name"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <input
                value={form.town}
                onChange={(e) => setForm({ ...form, town: e.target.value })}
                placeholder="Town / city"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <select
                value={form.businessType}
                onChange={(e) => setForm({ ...form, businessType: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              >
                {BUSINESS_TYPES.map((item) => <option key={item}>{item}</option>)}
              </select>

              <select
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              >
                {GOALS.map((item) => <option key={item}>{item}</option>)}
              </select>

              <input
                value={form.offerTitle}
                onChange={(e) => setForm({ ...form, offerTitle: e.target.value })}
                placeholder="Offer title e.g. 20% Launch Offer"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <select
                value={form.destinationType}
                onChange={(e) => setForm({ ...form, destinationType: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              >
                {DESTINATIONS.map((item) => <option key={item}>{item}</option>)}
              </select>

              <textarea
                value={form.offerDetails}
                onChange={(e) => setForm({ ...form, offerDetails: e.target.value })}
                placeholder="Offer details / campaign message"
                className="min-h-[110px] rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400 md:col-span-2"
              />

              <input
                value={form.destinationUrl}
                onChange={(e) => setForm({ ...form, destinationUrl: e.target.value })}
                placeholder="Destination link"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 md:col-span-2"
              />

              <input
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                placeholder="Contact name"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone / WhatsApp"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 md:col-span-2"
              />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={saveCampaign}
                disabled={saving}
                className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save QR Campaign'}
              </button>

              <a
                href={`/smart-quote?service=QR%20Campaign&source=qr-campaign&business=${encodeURIComponent(form.businessName)}`}
                className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
              >
                Send to Smart Quote
              </a>
            </div>

            {savedCampaign && (
              <div className="mt-5 rounded-2xl border border-cyan-500/25 bg-cyan-950/10 p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                  Campaign saved
                </p>
                <p className="mt-2 text-xl font-black text-white">
                  Reference: {savedCampaign.reference}
                </p>
              </div>
            )}
          </section>

          <section className="space-y-6">
            <div className="rounded-[2rem] border border-purple-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(168,85,247,0.10)] backdrop-blur-xl">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
                QR Preview
              </p>
              <h2 className="mt-2 text-2xl font-black">{campaign.headline}</h2>
              <p className="mt-2 text-sm text-slate-400">{campaign.subheadline}</p>

              <div className="mt-6 grid gap-5 md:grid-cols-[0.75fr_1.25fr]">
                <div className="rounded-3xl border border-cyan-500/25 bg-cyan-950/10 p-4">
                  {qrDataUrl ? (
                    <img src={qrDataUrl} alt="Generated QR campaign code" className="w-full rounded-2xl border border-cyan-500/20 bg-black p-3" />
                  ) : (
                    <div className="flex aspect-square items-center justify-center rounded-2xl border border-zinc-800 bg-black text-sm text-slate-500">
                      QR preview loading
                    </div>
                  )}

                  {qrDataUrl && (
                    <a
                      href={qrDataUrl}
                      download={`${form.businessName || 'nexorax'}-qr-campaign.png`}
                      className="mt-3 flex justify-center rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
                    >
                      Download QR PNG
                    </a>
                  )}
                </div>

                <div className="rounded-3xl border border-zinc-800 bg-black/70 p-5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Destination</p>
                  <p className="mt-2 break-all text-xs text-cyan-200">{destination}</p>

                  <p className="mt-5 font-mono text-[10px] uppercase tracking-widest text-slate-500">CTA</p>
                  <p className="mt-2 text-xl font-black text-white">{campaign.cta}</p>

                  <p className="mt-5 font-mono text-[10px] uppercase tracking-widest text-slate-500">Best use</p>
                  <p className="mt-2 text-sm text-slate-300">{campaign.bestUse}</p>
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 backdrop-blur-xl">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                  Poster / flyer wording
                </p>
                <button
                  type="button"
                  onClick={() => copyText(`${campaign.headline}\n\n${campaign.posterCopy}\n\n${campaign.cta}`, 'Poster copy')}
                  className="rounded-xl border border-cyan-500/40 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-950/40"
                >
                  Copy Poster Copy
                </button>
              </div>

              <div className="mt-4 rounded-2xl border border-zinc-800 bg-black/70 p-5">
                <h3 className="text-2xl font-black text-cyan-200">{campaign.headline}</h3>
                <p className="mt-3 text-sm text-slate-300">{campaign.posterCopy}</p>
                <p className="mt-4 font-bold text-white">{campaign.cta}</p>
              </div>
            </div>

            <div className="rounded-[2rem] border border-purple-500/25 bg-black/60 p-6 backdrop-blur-xl">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
                  TikTok / social caption
                </p>
                <button
                  type="button"
                  onClick={() => copyText(campaign.tikTokCaption, 'TikTok caption')}
                  className="rounded-xl border border-purple-500/40 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-950/40"
                >
                  Copy Caption
                </button>
              </div>

              <div className="mt-4 rounded-2xl border border-zinc-800 bg-black/70 p-5 text-sm leading-relaxed text-slate-200">
                {campaign.tikTokCaption}
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}
